import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { Input } from 'rsuite';

type AutoGrowTextareaProps = {
  value: string;
  onChange?: (value: string, event: React.SyntheticEvent) => void;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
  style?: React.CSSProperties;
  className?: string;
};

/**
 * RSuite textarea that grows vertically to fit all content (no internal scroll).
 */
export default function AutoGrowTextarea({
  value,
  onChange,
  placeholder,
  disabled,
  minRows = 4,
  style,
  className,
}: AutoGrowTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    const computed = window.getComputedStyle(el);
    const lineHeight = parseFloat(computed.lineHeight) || 20;
    const paddingY =
      parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom);
    const borderY =
      parseFloat(computed.borderTopWidth) +
      parseFloat(computed.borderBottomWidth);
    const minHeight = lineHeight * minRows + paddingY + borderY;

    // Reset to 0 (not 'auto') — with overflow:hidden, height:auto collapses scrollHeight to 0.
    el.style.height = '0px';
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  }, [minRows]);

  useLayoutEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => adjustHeight());
    observer.observe(el);
    return () => observer.disconnect();
  }, [adjustHeight]);

  return (
    <Input
      as="textarea"
      rows={minRows}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      inputRef={(instance: HTMLTextAreaElement | null) => {
        textareaRef.current = instance;
        adjustHeight();
      }}
      style={{
        resize: 'none',
        overflow: 'hidden',
        lineHeight: 1.45,
        ...style,
      }}
    />
  );
}
